import gql from 'graphql-tag';

export const GetCustomer = gql`
  query Customer($customerId: String!) {
    Customer(customerId: $customerId) {
      id: customerId
      customerId
      name
      createdAt
      dob
      email
      isoCountryCode
      phoneNumber
      fdCustomerId
      defaultFdAccountId
      loyaltyId
      thLegacyCognitoId
      optedInLoyalty
      isFSUser
      giftCard {
        id: cardNumber
        cardNumber
      }
      creditCards {
        createdAt
        alias
        cardType
        expiryMonth
        expiryYear
        billingAddress {
          postalCode
        }
      }
      thLoyaltyTier {
        name
        points
      }
      thLoyaltyCard {
        barcode
        cardId
        cardFormat
        details {
          points
          rewardsRedemptionEnabled
          donationUpcharge
          scanAndPay
        }
      }
      rutrTimeline {
        events {
          type
        }
      }
      secondaryIdentifiers {
        identifier
        identifierType
      }
      fsAccounts {
        type
        status
        id
      }
    }
  }
`;

export const GetCustomerOrders = gql`
  query Customer($customerId: String!, $ordersResumeAfter: String) {
    Customer(customerId: $customerId) {
      id: customerId
      customerId # needed to query orders
      email
      isoCountryCode
      loyaltyId
      orders(resumeAfter: $ordersResumeAfter) {
        count
        orders {
          id: rbiOrderId
          createdAt
          cart {
            totalCents
            serviceMode
            storeAddress {
              country
            }
            storeDetails {
              timezone
            }
          }
          delivery {
            status
          }
          order {
            refund {
              approvedAmount
              refundedItems {
                name
              }
              adminUserEmail
            }
          }
          loyaltyTransaction {
            pointsEarned
            pointsBalance
            pointsUsed
            bonusPointsEarned
            rewardsUsed
          }
          paymentStatus
          paymentVersion
          status
        }
        resumeAfter
      }
      refundCounts {
        fullRefunds
        partialRefunds
        autoRefundable
        ordersForRefundPercentage
      }
    }
  }
`;

export const GetCustomerOrderStatusesByIds = gql`
  query Customer($customerId: String!, $orderIds: [String]!) {
    Customer(customerId: $customerId) {
      id: customerId
      customerId
      ordersByIds(orderIds: $orderIds) {
        orders {
          id: rbiOrderId
          delivery {
            status
          }
          paymentStatus
          status
        }
      }
    }
  }
`;

export const GetCustomerGiftcard = gql`
  query GetCustomerGiftcard($customerId: String!) {
    Customer(customerId: $customerId) {
      id: customerId
      customerId
      email
      fdCustomerId
      isoCountryCode
      giftCard {
        id: cardNumber
        cardNumber
        currencyCode
        currentBalance
        expiryMonth
        expiryYear
        fdAccountId
        transactions {
          amount
          currencyCode
          info
          transactionTime
        }
      }
    }
  }
`;

export const GetCustomerOffers = gql`
  query GetCustomerOffers(
    $customerId: String!
    $offersVersion: String
    $offersResumeAfter: String
  ) {
    Customer(customerId: $customerId) {
      id: customerId
      customerId
      email
      isoCountryCode
      loyaltyId
      name
      assignedOffers(offersVersion: $offersVersion, resumeAfter: $offersResumeAfter) {
        resumeAfter
        offers {
          agentEmail
          assignedDate
          challengeGoals
          challengeReward
          comments
          couponId
          expiryDate
          id
          isActive
          isPartnershipOffer
          partnerId
          partnerName
          loyaltyEngineId
          name
          offerTemplateId
          rbiOrderId
          reason
          redemptionDate
          redemptionSteps
          terms
          tokenId
        }
      }
      remediationReasons {
        label
        value
      }
      supportOffers(offersVersion: $offersVersion) {
        _id
        name
        configIncentive {
          loyaltyEngineId
        }
        loyaltyEngineId
        monetaryValueCents
        name
      }
    }
  }
`;

export const GetCustomerOffersVersion = gql`
  query GetCustomerOffersVersion($customerId: String!) {
    Customer(customerId: $customerId) {
      id: customerId
      customerId
      offersVersion
    }
  }
`;

export const GetCustomerRemediationLinks = gql`
  query GetCustomerRemediationLinks($customerId: String!, $remediationsResumeAfter: String) {
    Customer(customerId: $customerId) {
      id: customerId
      customerId
      remediations(resumeAfter: $remediationsResumeAfter) @connection(key: "remediationLinks") {
        remediations {
          amount
          remediationType
          transactionId
          transactionType
        }
        resumeAfter
      }
    }
  }
`;

export const GetCustomerRemediations = gql`
  query Customer($customerId: String!, $remediationsResumeAfter: String) {
    Customer(customerId: $customerId) {
      id: customerId
      customerId # needed to query orders
      email
      isoCountryCode
      loyaltyId
      pointsRemediations {
        amount
        created
        displayName
        remediationReason
        remediationType
        remediationComments
        secondaryTransactionId
        secondaryTransactionType
        supportEmail
        transactionId
        transactionType
        userId
      }
      remediations(resumeAfter: $remediationsResumeAfter) {
        remediations {
          amount
          created
          displayName
          remediationComments
          remediationReason
          remediationType
          sanityId
          supportEmail
          transactionId
          transactionType
          userId
        }
        resumeAfter
      }
      remediationReasons {
        label
        value
      }
    }
  }
`;

export const GetCustomerSupportOffers = gql`
  query GetCustomerSupportOffers($customerId: String!) {
    Customer(customerId: $customerId) {
      id: customerId
      customerId
      email
      isoCountryCode
      loyaltyId
      offersVersion
      remediationReasons {
        label
        value
      }
      supportOffers {
        _id
        name
        monetaryValueCents
        configIncentive {
          loyaltyEngineId
        }
        loyaltyEngineId
      }
      thLegacyCognitoId
      thLoyaltyCard {
        cardId
      }
    }
  }
`;

export const GetCustomerHockeyChallenges = gql`
  query GetHockeyChallenges($customerId: String!) {
    Customer(customerId: $customerId) {
      id: customerId
      customerId # needed to query orders
      email
      isoCountryCode
      name
      hockeyChallenges {
        userStats {
          userId
          username
          challenges {
            challengeNumber
            title
            description
            status
            rank
            rankChange
            correctPicks
            pickAccuracy
            totalUsers
            daysPlayed
          }
          season {
            title
            description
            rank
            rankChange
            correctPicks
            pickAccuracy
            totalUsers
            daysPlayed
          }
          postseason {
            title
            description
            rank
            rankChange
            correctPicks
            pickAccuracy
            totalUsers
            daysPlayed
          }
        }
        history {
          contestDate
          streak
          points
          picks {
            setId
            playerName
            correct
          }
          offers
        }
      }
    }
  }
`;

export const GetCustomerFSTransactions = gql`
  query GetCustomerFSTransactions($customerId: String!, $page: Int!) {
    Customer(customerId: $customerId) {
      id: customerId
      customerId
      email
      isoCountryCode
      isFSUser
      fsTransactions(page: $page) {
        totalNumberOfTransactions
        transactions {
          transactionId
          amount {
            currencyCode
            valueCents
          }
          neoStatus
          neoType
          createdAt
          loyaltyPoints
          neoCategory
          neoDescription
        }
      }
    }
  }
`;

export const GetCustomerStqStatus = gql`
  query GetCustomerStqStatus($customerId: String!) {
    Customer(customerId: $customerId) {
      id: customerId
      customerId
      rutrPassedSkillsTestTimestamp
      rutrFailedSkillsTestTimestamp
      rutrConsentTimestamp
    }
  }
`;

export const CreateEmailOtp = gql`
  mutation CreateEmailOTPForFS($customerId: String!, $input: CreateEmailOTPRequest!) {
    createEmailOTPForFS(customerId: $customerId, input: $input)
  }
`;

export const MigrateUserFS = gql`
  mutation MigrateUserFS($customerId: String!, $input: MigrateUserRequest!) {
    migrateUserFS(customerId: $customerId, input: $input)
  }
`;
